import { AxiosError } from 'axios'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Input, Textarea } from 'components/Form'
import { Modal } from 'components/Modal/Modal'
import { CostCenterSelect } from 'components/Select/CostCenter.select'
import { DepartmentSelect } from 'components/Select/Department.select'
import { InventorySupplierSelect } from 'components/Select/InventorySupplier.select'
import { Select } from 'components/Select/Select'
import { UnitSelect } from 'components/Select/UnitSelect'
import { Button } from 'components/ui/Button/Button'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { PROCUREMENT_PRIORITY_META } from 'constants/meta'
import useModalClose from 'hooks/useModalClose'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { CursorPointer, Flex, ModalScrollContainer } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import {
  IRaiseProcurementRequestModalProps,
  IRaiseProcurementRequestModalState,
} from 'types/inventoryOps.interface'
import {
  CreateProcurementPurchaseRequestDto,
  ProcurementPriority,
  ProcurementPurchaseRequestListResponse,
} from 'types/procurement'
import { PurchaseRequestItemType } from 'types/procurement/enums'
import { baseUrl } from 'utils/axios'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { ItemLineCard, ItemLineMeta, RaiseModalHost } from './RaiseProcurementRequestModal.styled'

export const RaiseProcurementRequestModal = ({
  context,
  closeModal,
}: IRaiseProcurementRequestModalProps) => {
  const [state, setState] = useState<IRaiseProcurementRequestModalState>({
    title: `Reorder — ${context.name}`,
    department: null,
    costCenter: null,
    priority: null,
    supplier: null,
    reason: '',
    itemName: context.name,
    quantity: context.suggestedQty,
    unit: null,
    notes: `Auto-suggested from stock levels: on hand ${context.onHand}, min ${context.minLevel}`,
  })
  const [isSaving, setIsSaving] = useState(false)
  useFocusFirstInput()
  useModalClose(closeModal)

  const isCritical = state.priority?.value === ProcurementPriority.Critical

  const invalidMessage = (): string | null => {
    if (!state.title.trim()) return 'Enter a title'
    if (!state.department?.value) return 'Pick a department'
    if (!state.priority?.value) return 'Pick a priority'
    if (isCritical && !state.reason.trim()) return 'Explain why this request is critical'
    if (!state.itemName.trim()) return 'Enter an item name'
    if (!state.quantity || state.quantity <= 0) return 'Enter a quantity greater than zero'
    if (!state.unit?.value) return 'Pick a unit'
    return null
  }

  const constructData = (): CreateProcurementPurchaseRequestDto => {
    const items = [
      {
        type: context.inventoryItemId
          ? PurchaseRequestItemType.Item
          : PurchaseRequestItemType.Text,
        inventoryItemId: context.inventoryItemId,
        name: state.itemName,
        quantity: Number(state.quantity),
        unitId: state.unit?.value as number,
        urgency: null,
        order: 1,
      },
    ]
    const data: CreateProcurementPurchaseRequestDto = {
      title: state.title,
      priority: state.priority?.value as ProcurementPriority,
      departmentId: state.department?.value as number,
      items,
    }
    if (isCritical) {
      data.reason = state.reason
    }
    if (state.notes) {
      data.notes = state.notes
    }
    if (state.supplier?.value) {
      data.preferredSupplierId = state.supplier?.value as number
    }
    if (state.costCenter?.value) {
      data.costCenterId = state.costCenter?.value as number
    }
    return data
  }

  const handleSave = async () => {
    const message = invalidMessage()
    if (message) {
      toast.warn(message)
      return
    }
    try {
      setIsSaving(true)
      await baseUrl.post<ProcurementPurchaseRequestListResponse>(
        '/procurement/management/purchase/request',
        constructData(),
      )
      toast.success('Purchase request created — continue in Procurement')
      closeModal()
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.message)
      } else {
        toast.error(error.message)
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <RaiseModalHost>
      <Modal black>
        <Flex between width='100%' center mb='16'>
          <TitleText weight='bold'>Raise purchase request</TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <ModalScrollContainer>
          <Flex column gap='1rem' width='100%'>
            <Input
              label='Title'
              name='title'
              type='text'
              placeholder='Enter value...'
              value={state.title}
              onChange={(e) => setState({ ...state, title: e.target.value })}
            />
            <Flex gap='1rem' width='100%'>
              <DepartmentSelect
                value={state.department}
                onChange={(value) => setState({ ...state, department: value })}
                label='Department'
                placeholder='Select department'
              />
              <CostCenterSelect
                value={state.costCenter}
                onChange={(value) => setState({ ...state, costCenter: value })}
                label='Cost Center'
                placeholder='Select cost center'
              />
            </Flex>
            <Flex gap='1rem' width='100%'>
              <Select
                value={state.priority}
                label='Priority'
                placeholder='Select priority'
                options={Object.values(ProcurementPriority)
                  .filter((priority) => !isNaN(Number(priority)))
                  .map((priority) => ({
                    label: PROCUREMENT_PRIORITY_META[priority]?.label,
                    value: priority,
                  }))}
                onChange={(value) => setState({ ...state, priority: value })}
              />
              <InventorySupplierSelect
                value={state.supplier}
                onChange={(value) => setState({ ...state, supplier: value })}
                label='Preferred Supplier'
                placeholder='Select supplier'
              />
            </Flex>
            {isCritical && (
              <Input
                label='Why this request is critical?'
                name='reason'
                type='text'
                placeholder='Enter here...'
                value={state.reason}
                onChange={(e) => setState({ ...state, reason: e.target.value })}
              />
            )}
            <ItemLineCard>
              <Flex gap='0.5rem' center>
                {context.inventoryItemId ? (
                  <StatusPill status='success'>{`SKU-${context.sku}`}</StatusPill>
                ) : (
                  <>
                    <StatusPill status='accent'>Free-line text</StatusPill>
                    <ItemLineMeta>{context.sku}</ItemLineMeta>
                  </>
                )}
              </Flex>
              <Input
                label='Item name'
                name='itemName'
                type='text'
                placeholder='Enter item name'
                value={state.itemName}
                readOnly={Boolean(context.inventoryItemId)}
                onChange={(e) => setState({ ...state, itemName: e.target.value })}
              />
              <Flex gap='1rem' width='100%'>
                <Input
                  label='Quantity'
                  name='quantity'
                  type='number'
                  placeholder='Enter value...'
                  value={state.quantity}
                  onChange={(e) =>
                    setState({
                      ...state,
                      quantity: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                />
                <UnitSelect
                  value={state.unit}
                  onChange={(value) => setState({ ...state, unit: value })}
                  label='Unit'
                  placeholder='Select unit'
                />
              </Flex>
            </ItemLineCard>
            <Textarea
              label='Notes'
              name='notes'
              placeholder='Enter value...'
              value={state.notes}
              onChange={(e) => setState({ ...state, notes: e.target.value })}
            />
          </Flex>
        </ModalScrollContainer>
        <Flex width='100%' justcenter mt='16'>
          <Button variant='primary' disabled={isSaving} onClick={handleSave}>
            Create request
          </Button>
        </Flex>
      </Modal>
    </RaiseModalHost>
  )
}
